import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import {
  AnalyticsActiveFilterType,
  IExperienceDashboardFilters,
} from "@src/store/types/dashboardFilters";

import {
  getAnalyticsViewTypeChip,
  getChannelTypeChip,
  getCountryCodesChip,
  getCustomerTypeChip,
  getCustomEventsChip,
  getDeviceTypeChip,
  getEndTimeChip,
  getExcludeFreeOrdersChip,
  getIsZScoreEnabledChip,
  getLandingPageChip,
  getLandingPageFiltersChip,
  getMinMaxValueChip,
  getReachedCheckoutStageChip,
  getShippingMethodsChip,
  getStartTimeChip,
  getUrlParamChip,
  getUserBehaviorChip,
  getVisitorTypeChip,
} from "./activeFilterChipDerivers";

interface UseActiveFilterChipsArgs {
  dashboardFilters: IExperienceDashboardFilters;
  experience?: ExperienceFe;
  timezone: string;
  isUpsell: boolean;
  storeCurrency: { code: string; locale: string };
}

/**
 * Builds the "active filter" chip list from the current dashboard filters.
 * Shared by the authed analytics header and the share view so the two can't
 * drift — both render the same chips for the same filters.
 */
export const useActiveFilterChips = ({
  dashboardFilters,
  experience,
  timezone,
  isUpsell,
  storeCurrency,
}: UseActiveFilterChipsArgs): AnalyticsActiveFilterType[] => {
  return [
    getAnalyticsViewTypeChip(dashboardFilters.analyticsViewType, isUpsell),
    getIsZScoreEnabledChip(
      dashboardFilters.isZScoreEnabled,
      dashboardFilters.maxZScore
    ),
    getExcludeFreeOrdersChip(dashboardFilters.excludeFreeOrders),
    getDeviceTypeChip(dashboardFilters.deviceType),
    getCustomerTypeChip(dashboardFilters.customerType),
    getVisitorTypeChip(dashboardFilters.visitorType),
    getChannelTypeChip(dashboardFilters.channels),
    getCustomEventsChip(dashboardFilters.customEvents),
    getCountryCodesChip(
      dashboardFilters.countryCodes,
      dashboardFilters.countryCodesFilterType
    ),
    getEndTimeChip(dashboardFilters.endTime, timezone, experience),
    getStartTimeChip(dashboardFilters.startTime, timezone, experience),
    getUserBehaviorChip(dashboardFilters.userBehavior),
    getReachedCheckoutStageChip(dashboardFilters.reachedCheckoutStage),
    getUrlParamChip(dashboardFilters.urlParam),
    getLandingPageChip(dashboardFilters.landingPage),
    getLandingPageFiltersChip(dashboardFilters.landingPageFilters),
    getMinMaxValueChip(
      "minNetRevenue",
      dashboardFilters.minNetRevenue,
      storeCurrency
    ),
    getMinMaxValueChip(
      "maxNetRevenue",
      dashboardFilters.maxNetRevenue,
      storeCurrency
    ),
    getMinMaxValueChip(
      "minShippingRevenue",
      dashboardFilters.minShippingRevenue,
      storeCurrency
    ),
    getMinMaxValueChip(
      "maxShippingRevenue",
      dashboardFilters.maxShippingRevenue,
      storeCurrency
    ),
    getShippingMethodsChip(dashboardFilters.shippingMethods),
  ].filter((chip) => chip !== null);
};
